import { generatePath } from '@helpers/utils/paths/paths';
import { z } from 'zod';

import { KgInsightThemeType, themeTypeSchema } from './kg-insights';
import { timeSeriesDataSchema } from './timeseries-schema';

const themeOverviewResponseSchema = z.object({
  name: z.string(),
  description: z.string(),
  logo_url: z.string().optional(),
});

export type ThemeOverviewResponse = z.infer<typeof themeOverviewResponseSchema>;

export const themePerformanceSchema = z.object({
  index: z.string().datetime(),
  value: z.number(),
});

const entitiesSchema = z.record(
  z.string(),
  z.array(themePerformanceSchema).nullable()
);

export const calculatorResponseSchema = z.object({
  entities: entitiesSchema,
  aggregate: z.array(themePerformanceSchema),
});

export type CalculatorResponse = z.infer<typeof calculatorResponseSchema>;

const countrySchema = z.object({
  code: z.string(),
  name: z.string(),
  evidence: z.string(),
});

export const countryExposureSchema = z.object({
  result: z.array(countrySchema),
});

export type CountryExposureResponse = z.infer<typeof countryExposureSchema>;

const relationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  rank_change: z.number().nullish(),
  evidence: z.array(z.string()).nullish(),
  source_evidence: z.string().nullish(),
});

const parentSchema = z.object({
  id: z.string(),
  name: z.string(),
  // optional for non industry themes
  logo_url: z.string().optional(),
});

const themeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  // optional for non industry themes
  logo_url: z.string().optional(),
  parent: parentSchema,
});

export const themeDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  parent_theme: parentSchema,
  type: themeTypeSchema,
  // optional for non industry themes
  logo_url: z.string().optional(),
  metadata: z
    .object({
      direction_type: z.enum(['Contextual', 'Neutral', 'Intuitive']).optional(),
      intuitive_direction: z.enum(['Positive', 'Negative']).optional(),
      ms_attached_themes: z
        .array(
          z.object({
            rank: z.number(),
            theme_id: z.string(),
            theme_name: z.string(),
          })
        )
        .optional(),
      ms_basket_ticker: z.string().optional(),
      ms_primary_theme_id: z.string().optional(),
    })
    .optional(),
});

export type ThemeSchemaResponse = z.infer<typeof themeDetailsSchema>;

export const themeSearchParamsSchema = z.object({
  q: z.string(),
  types: themeTypeSchema.array().optional(),
  limit: z.number().optional(),
});
export type ThemeSearchParams = z.infer<typeof themeSearchParamsSchema>;

export const themeSearchResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: themeTypeSchema,
});
export type ThemeSearchResult = z.infer<typeof themeSearchResultSchema>;

export const themeSearchResponseSchema = z.array(themeSearchResultSchema);
export type ThemeSearchResponse = z.infer<typeof themeSearchResponseSchema>;

export const themeExposureSchema = z.object({
  theme: themeSchema,
  relationship: relationshipSchema,
});

export type ThemeExposure = z.infer<typeof themeExposureSchema>;

export const themeExposureResponseSchema = z.array(themeExposureSchema);

export type ThemeExposureResponse = z.infer<typeof themeExposureResponseSchema>;

const themeCompetitorResponseSchema = z.object({
  company: z.string(),
  theme: themeSchema,
  competitors: z.array(z.string()),
});
export type ThemeCompetitorResponse = z.infer<
  typeof themeCompetitorResponseSchema
>;

export const themeCompanySchema = z.object({
  company: z.string(),
  relationship: relationshipSchema,
});
export type ThemeCompany = z.infer<typeof themeCompanySchema>;

const themeCompaniesResponseSchema = z.array(themeCompanySchema);
export type ThemeCompaniesResponse = z.infer<
  typeof themeCompaniesResponseSchema
>;

export const themeChildSchema = z.object({
  theme: themeSchema,
  company_count: z.number(),
  is_company_exposed: z.boolean(),
});
export type ThemeChild = z.infer<typeof themeChildSchema>;

const themeChildrenResponseSchema = z.array(themeChildSchema);
export type ThemeChildrenResponse = z.infer<typeof themeChildrenResponseSchema>;

export const postSuggestedThemesRequestSchema = z.object({
  entity_tags: z.array(z.string()).optional(),
});
export type PostSuggestedThemesRequest = z.infer<
  typeof postSuggestedThemesRequestSchema
>;

export const postSuggestedThemesResponseSchema = z.object({
  theme_ids: z.array(z.string()),
});
export type PostSuggestedThemesResponse = z.infer<
  typeof postSuggestedThemesResponseSchema
>;

const companyCompetitorsWithThemesSchema = z.object({
  company: z.string(),
  relationship: relationshipSchema,
  competing_themes: z.array(themeSchema),
});
const companyCompetitorsWithThemesResponseSchema = z.array(
  companyCompetitorsWithThemesSchema
);
export type CompanyCompetitorsWithThemesResponse = z.infer<
  typeof companyCompetitorsWithThemesResponseSchema
>;

const countryExposureRelationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  rank_change: z.number().nullish(),
  source_evidence: z.string(),
  evidence: z.array(z.string()).optional(),
});

const countryExposureItemSchema = z.object({
  code: z.string(),
  relationship: countryExposureRelationshipSchema,
});

const companyCountryExposureResponseSchema = z.array(countryExposureItemSchema);

export type CompanyCountryExposureItem = z.infer<
  typeof countryExposureItemSchema
>;
export type CompanyCountryExposureResponse = z.infer<
  typeof companyCountryExposureResponseSchema
>;

const regionExposureRelationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  rank_change: z.number().nullish(),
  source_evidence: z.string(),
  evidence: z.array(z.string()).optional(),
});

const regionExposureItemSchema = z.object({
  name: z.string(),
  icon_url: z.string().url(),
  relationship: regionExposureRelationshipSchema,
});

const companyRegionExposureResponseSchema = z.array(regionExposureItemSchema);

const brandAndProductItemSchema = z.object({
  product: z.string(),
  relationship: relationshipSchema,
});
export type BrandAndProduct = z.infer<typeof brandAndProductItemSchema>;

const brandsAndProductsResponseSchema = z.array(brandAndProductItemSchema);
export type BrandsAndProductsResponse = z.infer<
  typeof brandsAndProductsResponseSchema
>;

export type CompanyRegionExposureItem = z.infer<
  typeof regionExposureItemSchema
>;
export type CompanyRegionExposureResponse = z.infer<
  typeof companyRegionExposureResponseSchema
>;

const themePerformanceSortHorizonSchema = z.enum([
  'intraday',
  '1d',
  '1w',
  '1m',
  '3m',
  '6m',
  '1y',
]);

export type ThemePerformanceSortHorizon = z.infer<
  typeof themePerformanceSortHorizonSchema
>;

const themePerformanceSubClassSchema = z.enum(['short', 'long']);
export type ThemePerformanceSubClass = z.infer<
  typeof themePerformanceSubClassSchema
>;

export interface ThemePerformanceParams {
  ids?: string;
  sort_order?: string;
  sort_horizon?: ThemePerformanceSortHorizon;
  theme_type?: KgInsightThemeType;
  page?: number;
  page_size?: number;
  min_assets?: number;
}

// V4 Theme Performance Schemas
// can be closed, open, intraday and mb more
export const marketStatusSchema = z.string();

export const headlineHorizonSchema = z.enum(['intraday', '1d']);

export type HeadlineHorizon = z.infer<typeof headlineHorizonSchema>;

const performanceMapSchema = z.object({
  intraday: z.number().optional(),
  '1d': z.number(),
  '1w': z.number(),
  '1m': z.number(),
  '3m': z.number(),
  '6m': z.number(),
  '1y': z.number(),
});

const intradayMetadataV4Schema = z.object({
  last_updated: z.string(),
  headline_horizon: headlineHorizonSchema,
});

const marketMetadataV4Schema = z.object({
  market_status: marketStatusSchema,
});

const themeBasketSchema = z.object({
  id: z.string(),
  entities: z.array(z.string()).nullable(),
  performance: performanceMapSchema,
  intraday_metadata: intradayMetadataV4Schema,
  market_metadata: marketMetadataV4Schema,
  sub_class: z.enum(['all', 'long', 'short']),
  theme_type: themeTypeSchema.optional(),
});

export type ThemePerformanceBasket = z.infer<typeof themeBasketSchema>;

const themePerformanceResponseSchema = z.object({
  themes: z.array(themeBasketSchema),
  page: z.number(),
  size: z.number(),
});

export type ThemePerformanceResponse = z.infer<
  typeof themePerformanceResponseSchema
>;

const themeTimeseriesPointSchema = z.object({
  index: z.iso.datetime(),
  value: z.number(),
});

const themeTimeseriesResponseSchema = z.object({
  aggregate: z.array(themeTimeseriesPointSchema),
  market_metadata: marketMetadataV4Schema,
});

export type ThemeTimeseriesResponse = z.infer<
  typeof themeTimeseriesResponseSchema
>;

export type ThemeTimeseriesPoint = z.infer<typeof themeTimeseriesPointSchema>;

export const themeOverview = {
  getThemeV3: {
    path: (themeId: string) => `theme/v3/${themeId}`,
    schema: themeDetailsSchema,
  },
  searchThemes: {
    path: ({ q, types, limit }: ThemeSearchParams) => {
      const params = new URLSearchParams({ q });

      if (types !== undefined) {
        params.set('types', types.join(','));
      }

      if (limit !== undefined) {
        params.set('limit', String(limit));
      }

      return `theme/v3/search?${params.toString()}`;
    },
    schema: themeSearchResponseSchema,
  },
  themeCalculator: {
    path: 'calculator/v1/performance',
    schema: calculatorResponseSchema,
  },
  getV7ThemePerformance: {
    path: (params?: ThemePerformanceParams) => {
      const basePath = 'entity-performance/v7/theme';

      if (!params) {
        return basePath;
      }

      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value != null)
      );

      if (Object.keys(filteredParams).length === 0) {
        return basePath;
      }

      return generatePath({
        basePath,
        params: filteredParams,
      });
    },
    schema: themePerformanceResponseSchema,
  },
  getV7ThemePerformanceTimeseries: {
    path: ({
      themeId,
      horizon,
      subClass,
    }: {
      themeId: string;
      horizon: ThemePerformanceSortHorizon;
      subClass?: ThemePerformanceSubClass;
    }) =>
      generatePath({
        basePath: `entity-performance/v7/theme/${themeId}/timeseries`,
        params: {
          horizon,
          ...(subClass && { sub_class: subClass }),
        },
      }),
    schema: themeTimeseriesResponseSchema,
  },
  spxBenchmark: {
    path: 'ca/v3/q',
    schema: timeSeriesDataSchema,
  },
  // v3 / v6
  getCompanyTheme: {
    path: (entityTag: string, limit?: number, version: 'v3' | 'v6' = 'v3') => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/${version}/company/${entityTag}/theme-exposure${limitParam}`;
    },
    schema: themeExposureResponseSchema,
  },
  getCompanyCompetitors: {
    path: (entityTag: string, themeId: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/theme/${themeId}/competitors${limitParam}`;
    },
    schema: themeCompetitorResponseSchema,
  },
  getThemeChildren: {
    path: (themeId: string) => `ontology/v3/theme/${themeId}/children`,
    schema: themeChildrenResponseSchema,
  },
  postSuggested: {
    path: 'ontology/v4/suggested-themes',
    schema: postSuggestedThemesResponseSchema,
  },
  getThemeCompanies: {
    path: (themeId: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/theme/${themeId}/company-exposure${limitParam}`;
    },
    schema: themeCompaniesResponseSchema,
  },
  getCompanyCompetitorsWithThemes: {
    path: (entityTag: string, limit?: number, version: 'v3' | 'v6' = 'v3') => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/${version}/company/${entityTag}/competitors${limitParam}`;
    },
    schema: companyCompetitorsWithThemesResponseSchema,
  },
  getCompanyCountryExposure: {
    path: (entityTag: string) =>
      `ontology/v3/company/${entityTag}/country-exposure`,
    schema: companyCountryExposureResponseSchema,
  },
  getCompanyRegionExposure: {
    path: (entityTag: string) =>
      `ontology/v3/company/${entityTag}/region-exposure`,
    schema: companyRegionExposureResponseSchema,
  },
  getBrandsAndProducts: {
    path: (entityTag: string) => `ontology/v3/company/${entityTag}/products`,
    schema: brandsAndProductsResponseSchema,
  },
};
